import { BranchIcon } from '@persona-id/design-tokens/icons';
import { memo, ReactNode, VFC } from 'react';
import styled from 'styled-components';

import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import Tooltip from 'design-system/components/Tooltip';
import { Elevation } from 'design-system/variables/effect';

export const CONDITIONAL_ROOT_SIZE = 108;

const NodeButton = styled(Button).attrs(() => ({ type: 'button' }))<{
  selected: boolean;
  hasError?: boolean;
}>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: ${CONDITIONAL_ROOT_SIZE}px;
  width: ${CONDITIONAL_ROOT_SIZE}px;
  padding: 6px;
  border: 2px solid;
  border-radius: 12px;
  box-shadow: ${Elevation.Mid};
  cursor: pointer;
  pointer-events: auto;
  user-select: none;
  z-index: ${Z_INDICES.withShadow};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-color: ${({ theme, selected, hasError }) => {
    if (hasError) {
      return theme.color.variants.danger.default;
    } else if (selected) {
      return theme.color.primary.brand600;
    } else {
      return theme.color.variants.primary.highlight;
    }
  }};
  svg {
    color: ${({ theme }) => theme.color.primary.brand600};
    height: 44px;
    width: 34px;
    transform: rotate(270deg);
  }
  &:focus:enabled {
    background-color: ${({ theme }) => theme.color.background.primary};
  }
`;

export interface ConditionalRootNodeProps {
  hasError?: boolean;
  selected?: boolean;
  nodeHighlighter?: ReactNode;
  tooltipContents?: ReactNode;
}

/**
 * This component is responsible for rendering a ConditionalRootNode
 * representing a single conditional workflow step
 */
const ConditionalRootNode: VFC<ConditionalRootNodeProps> = ({
  hasError = false,
  selected = false,
  nodeHighlighter,
  tooltipContents,
}) => {
  return (
    <Tooltip
      side='right'
      trigger={
        <NodeButton
          data-test='conditional-root-node__container'
          selected={selected}
          hasError={hasError}
        >
          {nodeHighlighter}
          <BranchIcon size={24} />
        </NodeButton>
      }
      disabled={tooltipContents == null}
    >
      {tooltipContents}
    </Tooltip>
  );
};

export default memo(ConditionalRootNode);
